import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { 
  collection, query, where, onSnapshot, doc, getDoc 
} from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { 
  IoTimerOutline, IoCheckboxOutline, IoWalletOutline, IoTrendingUpOutline,
  IoNotificationsOutline, IoCloseOutline, IoArrowForward, IoPersonCircleOutline
} from 'react-icons/io5';
import '../screens/HomeScreen.css'; // Leverage general homescreen classes
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';
import kondifyLogo from '../assets/Kondify inverso transparente.png';

// HomeCard shared component replica for styling consistency
const HomeCard = ({ to, icon, title, theme, onClick, badgeCount }) => {
  const content = (
    <>
      {badgeCount > 0 && (
        <div className="ks-card-badge">{badgeCount > 99 ? '99+' : badgeCount}</div>
      )}
      <div className={`ks-card-icon ${theme}`}>{icon}</div>
      <div className="ks-card-body">
        <h3>{title}</h3>
      </div>
    </>
  );

  if (onClick) {
    return <div className="ks-card" onClick={onClick}>{content}</div>;
  }

  return <Link to={to} className="ks-card">{content}</Link>;
};

// Avatar viewer helper replica
const AvatarViewer = ({ avatarUrl, className, size }) => {
  return (
    <img 
      src={avatarUrl} 
      alt="Avatar" 
      className={className} 
      style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} 
    />
  );
};

function TrabajadorDashboardScreen() {
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();

  const [condoNombre, setCondoNombre] = useState('Mi Condominio');
  const [showNotifications, setShowNotifications] = useState(false);

  const [asistenciaHoy, setAsistenciaHoy] = useState(null);
  const [reportesAsignados, setReportesAsignados] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [reminderMsg, setReminderMsg] = useState('');

  useEffect(() => {
    if (!currentUser || !userData?.condominioId) return;

    // Fetch employee data for schedule
    const qEmp = query(
      collection(db, "empleados"),
      where("usuarioId", "==", currentUser.uid)
    );
    const unsubEmp = onSnapshot(qEmp, (snap) => {
      if (!snap.empty) {
        setSchedule(snap.docs[0].data());
      }
    });

    // Fetch condo name
    getDoc(doc(db, "condominios", userData.condominioId)).then(snap => {
      if (snap.exists()) {
        setCondoNombre(snap.data().nombre || 'Mi Condominio');
      } else if (userData.condominioId === 'GLOBAL') {
        setCondoNombre('Corporativo Kondify');
      }
    });

    // 1. Asistencia de hoy
    const hoyStr = new Date().toISOString().split('T')[0];
    const qAsistencia = query(
      collection(db, "asistencia_trabajadores"),
      where("trabajadorId", "==", currentUser.uid),
      where("fecha", "==", hoyStr)
    );
    const unsubAsistencia = onSnapshot(qAsistencia, (snap) => {
      if (!snap.empty) {
        setAsistenciaHoy(snap.docs[0].data());
      } else {
        setAsistenciaHoy(null);
      }
    });

    // 2. Reportes asignados pendientes
    const qReportes = query(
      collection(db, "reportes"),
      where("condominioId", "==", userData.condominioId),
      where("asignadoA", "==", currentUser.uid),
      where("status", "!=", "completado")
    );
    const unsubReportes = onSnapshot(qReportes, (snap) => {
      setReportesAsignados(snap.docs.map(d => d.data()));
    });

    // 3. Actividades diarias incompletes
    const qActividades = query(
      collection(db, "actividades_diarias"),
      where("condominioId", "==", userData.condominioId)
    );
    const unsubActividades = onSnapshot(qActividades, (snap) => {
      const list = snap.docs.map(d => d.data());
      const incomplete = list.filter(act => {
        const completedToday = act.completadoPor && act.completadoPor[hoyStr];
        return !completedToday;
      });
      setActividades(incomplete);
    });

    return () => {
      unsubAsistencia();
      unsubReportes();
      unsubActividades();
      unsubEmp();
    };
  }, [currentUser, userData]);

  useEffect(() => {
    if (!schedule) return;
    const checkScheduleReminders = () => {
      const now = new Date();
      const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const diaHoy = diasSemana[now.getDay()];

      let isWorkingDay = false;
      let targetEntrada = '';
      let targetSalida = '';

      if (schedule.horariosDetalle && schedule.horariosDetalle[diaHoy]) {
        const det = schedule.horariosDetalle[diaHoy];
        isWorkingDay = det.activo;
        targetEntrada = det.entrada;
        targetSalida = det.salida;
      } else {
        isWorkingDay = schedule.diasLaborales && schedule.diasLaborales.includes(diaHoy);
        targetEntrada = schedule.horaEntrada;
        targetSalida = schedule.horaSalida;
      }

      if (isWorkingDay) {
        const currentMinutes = now.getHours() * 60 + now.getMinutes();

        // 1. Recordatorio Entrada (15 min antes hasta 30 min después)
        if (!asistenciaHoy && targetEntrada) {
          const [hE, mE] = targetEntrada.split(':').map(Number);
          const minutesEntrada = hE * 60 + mE;
          if (currentMinutes >= minutesEntrada - 15 && currentMinutes <= minutesEntrada + 30) {
            setReminderMsg(`⏰ ¡Jornada iniciada! Recuerda registrar tu Check-In (Entrada programada: ${targetEntrada}).`);
            return;
          }
        }

        // 2. Recordatorio Salida (15 min antes hasta 30 min después)
        if (asistenciaHoy && !asistenciaHoy.checkOut && targetSalida) {
          const [hS, mS] = targetSalida.split(':').map(Number);
          const minutesSalida = hS * 60 + mS;
          if (currentMinutes >= minutesSalida - 15 && currentMinutes <= minutesSalida + 30) {
            setReminderMsg(`⏰ ¡Fin de turno! Recuerda registrar tu Check-Out (Salida programada: ${targetSalida}).`);
            return;
          }
        }
      }
      setReminderMsg('');
    };

    checkScheduleReminders();
    const interval = setInterval(checkScheduleReminders, 60000);
    return () => clearInterval(interval);
  }, [schedule, asistenciaHoy]);

  const getPendingModules = () => {
    const list = [];
    if (!asistenciaHoy) {
      list.push({
        id: 'asistencia',
        name: 'Control de Asistencia',
        count: 1,
        desc: 'Registrar entrada de hoy',
        path: '/trabajador/asistencia',
        icon: <IoTimerOutline size={20} color="#7B61FF" />
      });
    }
    if (actividades.length > 0) {
      list.push({
        id: 'actividades',
        name: 'Actividades Diarias',
        count: actividades.length,
        desc: 'Actividades programadas sin checar',
        path: '/trabajador/actividades',
        icon: <IoCheckboxOutline size={20} color="#10b981" />
      });
    }
    if (reportesAsignados.length > 0) {
      list.push({
        id: 'reportes',
        name: 'Reportes Asignados',
        count: reportesAsignados.length,
        desc: 'Incidencias pendientes de resolver',
        path: '/trabajador/actividades',
        icon: <IoNotificationsOutline size={20} color="#ea580c" />
      });
    }
    return list;
  };

  const pendingList = getPendingModules();
  const globalBadgeCount = pendingList.reduce((acc, curr) => acc + curr.count, 0);

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/" />

      <main className="ks-main">
        <div className="ks-scroll-area">
          
          {/* ── HEADER TOP BAR ── */}
          <div className="ks-home-header-sticky-wrapper">
            <div className="native-safe-area-top" style={{ background: 'var(--kondo-purple)', height: '44px', display: 'block' }} />
            <div className="ks-top-bar">
              <div className="ks-top-bar-left">
                <div className="home-avatar-wrapper ks-top-avatar" onClick={() => navigate('/perfil')}>
                  {userData?.fotoPerfilUrl ? (
                    <AvatarViewer avatarUrl={userData.fotoPerfilUrl} className="home-profile-avatar" size={32} />
                  ) : (
                    <div className="home-profile-avatar-placeholder ks-top-avatar-placeholder">
                      <IoPersonCircleOutline size={32} color="#6348D1" />
                    </div>
                  )}
                </div>
                <div className="ks-top-greeting">
                  <span className="ks-top-saludo">¡Hola,</span>
                  <span className="ks-top-nombre">{userData?.nombre?.split(' ')[0] || 'Usuario'}!</span>
                </div>
              </div>

              <div className="ks-top-bar-right">
                <button onClick={() => setShowNotifications(true)} className="ks-top-icon-btn" title="Notificaciones" style={{ position: 'relative' }}>
                  <IoNotificationsOutline size={22} color="#1A1D21" />
                  {globalBadgeCount > 0 && (
                    <span className="ks-top-badge">{globalBadgeCount > 99 ? '99+' : globalBadgeCount}</span>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* ── HERO CARD ── */}
          <div className="ks-hero-card-wrap">
            <div className="ks-hero-card">
              
              <div className="ks-hero-brand-row">
                <div className="ks-hero-logo-wrap">
                  <img src={kondifyLogo} alt="Kondify" className="ks-hero-full-logo" />
                  <h2 className="ks-hero-condo-name">
                    {condoNombre}
                  </h2>
                </div>

                <div className="ks-hero-status-group">
                  <span className="ks-hero-unit-chip" style={{ textTransform: 'capitalize' }}>
                    {userData?.puesto || 'Conserje'}
                  </span>
                </div>
              </div>

              {globalBadgeCount > 0 && (
                <div className="ks-hero-stats-row">
                  <div className="ks-stat-chip" onClick={() => setShowNotifications(true)} style={{ gridColumn: '1 / -1' }}>
                    <span className="ks-stat-label">TAREAS PENDIENTES</span>
                    <span className="ks-stat-value">{globalBadgeCount} por atender</span>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* ── RECORDATORIO DE HORARIO (PUSH SIMULADO) ── */}
          {reminderMsg && (
            <div style={{
              margin: '0 16px 20px',
              padding: '12px 16px',
              background: '#fef3c7',
              borderLeft: '4px solid #d97706',
              borderRadius: '8px',
              color: '#92400e',
              fontSize: '0.9rem',
              fontWeight: '600',
              boxShadow: '0 4px 12px rgba(217, 119, 6, 0.1)',
              animation: 'pulse-glow 2s infinite alternate'
            }}>
              {reminderMsg}
            </div>
          )}

          {/* ── MODULE GRID ── */}
          <div className="ks-content-area">
            <div className="ks-modules-grid">
              
              <HomeCard 
                to="/trabajador/asistencia" 
                icon={<IoTimerOutline />} 
                title="Asistencia" 
                theme="theme-purple" 
                badgeCount={!asistenciaHoy ? 1 : 0} 
              />

              <HomeCard 
                to="/trabajador/actividades" 
                icon={<IoCheckboxOutline />} 
                title="Actividades" 
                theme="theme-green" 
                badgeCount={actividades.length + reportesAsignados.length} 
              />

              <HomeCard 
                to="/trabajador/nomina" 
                icon={<IoWalletOutline />} 
                title="Mi Nómina" 
                theme="theme-blue" 
              />

              <HomeCard 
                to="/trabajador/metas" 
                icon={<IoTrendingUpOutline />} 
                title="Metas" 
                theme="theme-orange" 
              />

            </div>
          </div>

        </div>

        {/* ── NOTIFICATIONS MODAL ── */}
        {showNotifications && (
          <div className="notifications-overlay" onClick={() => setShowNotifications(false)}>
            <div className="notifications-modal" onClick={e => e.stopPropagation()}>
              <div className="notifications-modal-header">
                <h2>Tareas Pendientes</h2>
                <button className="close-modal-btn" onClick={() => setShowNotifications(false)}>
                  <IoCloseOutline size={24} />
                </button>
              </div>
              <div className="notifications-list">
                {pendingList.length === 0 ? (
                  <div className="no-notifications">
                    <div style={{ padding: '20px', background: '#f4f2ff', borderRadius: '50%', marginBottom: '12px' }}>
                      <IoNotificationsOutline size={40} color="#6348D1" />
                    </div>
                    <p>¡Todo limpio! No tienes tareas pendientes.</p>
                  </div>
                ) : (
                  pendingList.map(mod => (
                    <div key={mod.id} className="notification-item"
                      onClick={() => { setShowNotifications(false); navigate(mod.path); }}>
                      <div className="notif-icon-box">{mod.icon}</div>
                      <div className="notif-content">
                        <strong>{mod.name}</strong>
                        <span>{mod.count} pendiente{mod.count > 1 ? 's' : ''}</span>
                      </div>
                      <IoArrowForward color="#9ca3af" style={{ marginLeft: 'auto' }} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

      </main>

      <KondoBottomNav activeRoute="/" />
    </div>
  );
}

export default TrabajadorDashboardScreen;
